import { useState, useEffect } from "react"
import { getDatabase, ref, onValue, set, get, update } from "firebase/database"
import { useNavigate } from "react-router-dom"
import { push } from "firebase/database"
import { getAuth } from "firebase/auth"

import { Bell, CheckCircle, History, ArrowDown, ArrowUp, Info, Map, Building, Layers, Truck } from "lucide-react"
import "./brandRates.css"
import InfoIcon from "./infoIcon"

const regionsWithAreas = {
  Punjab: [
    "Chandigarh",
    "Hoshiarpur",
    "Kharad",
    "Jallandhar",
    "Mohali",
    "Gurdaspur",
    "Pathankot",
    "Ludhiana",
    "Amritsar",
    "Patiala",
    "Firozpur",
    "Batala",
    "Bathinda",
    "Kapurthala",
    "Barnala",
  ],
  Himachal: [
    "Kangra",
    "Rampur Shimla",
    "Baddi",
    "Kinnour",
    "Kasauli",
    "Parwanu",
    "Sirmour",
    "Poanta sahib",
    "Solan",
    "Nalagarh",
    "Una",
    "Sahran",
    "Rohru",
    "Kullu",
    "Mandi",
    "Hamirpur",
    "Chamba",
    "Nurpur",
    "Bilashpur",
  ],
  Uttrakhand: [
    "Dehradun khuthal gate",
    "Rurki",
    "Haridwar",
    "Rishikesh",
    "Saharanpur",
    "Laksar",
    "vikasnagar",
    "Bijnour",
    "Heldwani",
    "Chamoli",
  ],
  Jharkhand: ["Jharkhand"],
  Jammu: ["Srinagar"],
  Haryana: ["Ambala", "Panchhkula", "fatehpur"],
}

const grades = ["OPC43", "OPC53", "PPC", "PSC"]

const BrandRates = ({ adminView = false, selectedBrand = "" }) => {
  const [pricingInfo, setPricingInfo] = useState({})
  const [selectedRegion, setSelectedRegion] = useState("")
  const [selectedArea, setSelectedArea] = useState("")
  const [supplierBrand, setSupplierBrand] = useState(adminView ? selectedBrand : localStorage.getItem("supplierBrand"))
  const [manualUpdates, setManualUpdates] = useState({})
  const [selectedGrade, setSelectedGrade] = useState("")
  const [showNotification, setShowNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState("")
  const [priceHistory, setPriceHistory] = useState({})
  const [showHistory, setShowHistory] = useState(false)
  const [allBrandsPricing, setAllBrandsPricing] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const navigate = useNavigate()
  const gifUrl = "/Images/New notification.gif"

  // Update supplierBrand when selectedBrand prop changes (for admin view)
  useEffect(() => {
    if (adminView) {
      setSupplierBrand(selectedBrand)
    }
  }, [adminView, selectedBrand])

  const sendBrandRateNotification = async (userId, region, area, brand, newRate) => {
    if (!userId || !region || !area || !brand || !newRate) {
      console.error("Missing parameters for brand rate notification:", { userId, region, area, brand, newRate });
      return false;
    }
  
    try {
      const db = getDatabase();
  
      // 🔄 Changed path to user_notifications/BrandRates/userId
      console.log(`Attempting to write notification to path: user_notifications/BrandRates/${userId}`);
      const notificationRef = ref(db, `user_notifications/BrandRates/${userId}`);
      const newNotificationRef = push(notificationRef);
  
      const title = `${brand} Rates Update`;
      const message = `Check new rate for ${brand} in ${area}, ${region} has been updated.`;
  
      const newNotification = {
        title,
        message,
        region,
        area,
        brand,
        newRate,
        timestamp: new Date().toISOString(),
        seen: false,
      };
  
      console.log("Notification data to be saved:", newNotification);
  
      await set(newNotificationRef, newNotification);
      console.log("✅ Brand rate notification sent successfully to path:", newNotificationRef.toString());
      return true;
    } catch (error) {
      console.error("❌ Error sending brand rate notification:", error.code, error.message);
      return false;
    }
  };
  
  useEffect(() => {
    if (adminView) {
      // Admin doesn't need brand check
      setLoading(false)
      return
    }

    const storedBrand = localStorage.getItem("supplierBrand")
    if (!storedBrand) {
      setError("Brand not selected. Please log in again.")
      navigate("/login")
      return
    }

    setSupplierBrand(storedBrand)
    setLoading(false)
  }, [navigate, adminView])

  useEffect(() => {
    if (selectedRegion && selectedArea) {
      if (adminView && !selectedBrand) {
        // For admin view with no brand selected, fetch all brands' pricing
        fetchAllBrandsPricing(selectedRegion, selectedArea)
      } else {
        // For admin view with brand selected or supplier view
        createNode(selectedRegion, selectedArea)
        const pricingRef = ref(getDatabase(), `rates/${selectedRegion}/${selectedArea}`)
        const unsubscribe = onValue(pricingRef, (snapshot) => {
          if (snapshot.exists()) {
            setPricingInfo(snapshot.val())
          } else {
            setPricingInfo({})
          }
        })
        return () => unsubscribe()
      }
    }
  }, [selectedRegion, selectedArea, adminView, selectedBrand])

  // Fetch all brands' pricing for admin view
  const fetchAllBrandsPricing = async (region, area) => {
    const db = getDatabase()
    const pricingRef = ref(db, `rates/${region}/${area}`)

    onValue(pricingRef, (snapshot) => {
      if (snapshot.exists()) {
        setAllBrandsPricing(snapshot.val())
      } else {
        setAllBrandsPricing({})
      }
    })
  }

  // Fetch price history when a grade is selected
  useEffect(() => {
    if (selectedRegion && selectedArea && selectedGrade && supplierBrand) {
      const historyRef = ref(
        getDatabase(),
        `rates_history/${selectedRegion}/${selectedArea}/${supplierBrand}/${selectedGrade}`,
      )
      const unsubscribe = onValue(historyRef, (snapshot) => {
        if (snapshot.exists()) {
          const historyData = snapshot.val()
          // Convert to array and sort by timestamp (newest first)
          const historyArray = Object.entries(historyData)
            .map(([key, value]) => ({
              id: key,
              ...value,
            }))
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

          setPriceHistory(historyArray)
        } else {
          setPriceHistory([])
        }
      })
      return () => unsubscribe()
    }
  }, [selectedRegion, selectedArea, selectedGrade, supplierBrand])

  const handleRegionChange = (e) => {
    setSelectedRegion(e.target.value)
    setSelectedArea("")
    setSelectedGrade("")
    setShowHistory(false)
  }

  const createNode = async (region, area) => {
    if (!region || !area || !supplierBrand) return

    const db = getDatabase()
    const pricingRef = ref(db, `rates/${region}/${area}`)
    const snapshot = await get(pricingRef)
    const existingData = snapshot.val() || {}

    if (!existingData[supplierBrand]) {
      const brandData = grades.reduce((acc, grade) => {
        acc[grade] = { price: "updated soon...", deliveryTime: "updated soon..." }
        return acc
      }, {})

      set(ref(db, `rates/${region}/${area}/${supplierBrand}`), brandData).catch((error) =>
        console.error("Error creating node:", error),
      )
    }
  }

  const handleManualUpdate = (field, value) => {
    setManualUpdates((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const updatePrice = async () => {
    if (!supplierBrand || !pricingInfo[supplierBrand] || !selectedGrade) {
      alert("You are not allowed to update this data.")
      return
    }

    const newPrice = manualUpdates.price || ""
    const newDeliveryTime = manualUpdates.deliveryTime || ""

    if (!newPrice || !newDeliveryTime) {
      alert("Please enter valid price and delivery time.")
      return
    }

    try {
      const db = getDatabase()
      const priceRef = ref(db, `rates/${selectedRegion}/${selectedArea}/${supplierBrand}/${selectedGrade}`)

      // Get current price data before updating
      const currentPriceSnapshot = await get(priceRef)
      const currentPriceData = currentPriceSnapshot.exists() ? currentPriceSnapshot.val() : null

      // Update the price
      await update(priceRef, {
        price: newPrice,
        deliveryTime: newDeliveryTime,
      })

      // Save to history
      if (currentPriceData) {
        const historyRef = ref(db, `rates_history/${selectedRegion}/${selectedArea}/${supplierBrand}/${selectedGrade}`)
        const newHistoryRef = push(historyRef)

        await set(newHistoryRef, {
          previousPrice: currentPriceData.price,
          previousDeliveryTime: currentPriceData.deliveryTime,
          newPrice: newPrice,
          newDeliveryTime: newDeliveryTime,
          timestamp: new Date().toISOString(),
          updatedBy: supplierBrand,
        })
      }

      // Get current user
      const auth = getAuth()
      let userId = null

      if (auth.currentUser) {
        userId = auth.currentUser.uid
        console.log("Current user ID:", userId)
      } else {
        console.warn("No user is currently logged in, using fallback ID")
        // Fallback to a generic ID for testing - remove this in production
        userId = "test_user_id"
      }

      const region = selectedRegion
      const area = selectedArea
      const brand = supplierBrand
      const newRate = newPrice

      // Send notification with better error handling
      const notificationSent = await sendBrandRateNotification(userId, region, area, brand, newRate)

      if (notificationSent) {
        console.log("Notification sent successfully")
        setNotificationMessage(`${brand} in ${area}, ${region} has been updated to ₹${newRate}`)
      } else {
        console.error("Failed to send notification")
        setNotificationMessage(`${brand} price updated, but notification could not be sent.`)
      }

      setShowNotification(true)

      // Reset form
      setManualUpdates({})

      // Auto-hide notification after 3 seconds
      setTimeout(() => {
        setShowNotification(false)
      }, 3000)
    } catch (error) {
      console.error("Error updating price:", error)
      setNotificationMessage(`Error: ${error.message || "Please try again."}`)
      setShowNotification(true)
      setTimeout(() => {
        setShowNotification(false)
      }, 5000)
    }
  }

  const toggleHistory = () => {
    setShowHistory(!showHistory)
  }

  const getPriceChangeIndicator = (previousPrice, newPrice) => {
    if (!previousPrice || !newPrice) return null

    const prev = Number.parseFloat(previousPrice)
    const current = Number.parseFloat(newPrice)

    if (current > prev) {
      return <ArrowUp className="price-increase" />
    } else if (current < prev) {
      return <ArrowDown className="price-decrease" />
    }
    return null
  }

  if (loading) {
    return <div className="loading-state">Loading brand rates...</div>
  }

  if (error) {
    return <div className="error-state">{error}</div>
  }

  return (
    <div className="brand-rates-container">
      <div className="brand-rates-card">
        <div className="brand-rates-header">
          <div className="brand-rates-title">
            <h2>
              {adminView
                ? `Manage Brand Rates ${selectedBrand ? `for ${selectedBrand}` : "for All Brands"}`
                : `Manage Brand Rates for ${supplierBrand}`}
              <InfoIcon text="This section displays the latest brand rates for cement and other materials. You can view and compare prices for different brands such as ACC, JSW, Shree, Ultratech, and JK Super. The rates are updated regularly based on market changes. If a rate is not available, it may be due to no current pricing for that brand." />
            </h2>
          </div>
        </div>
        <div className="brand-rates-body">
          {showNotification && (
            <div className="notification-alert">
              <Bell className="notification-icon" />
              <span>{notificationMessage}</span>
              <button className="close-button" onClick={() => setShowNotification(false)}>
                ×
              </button>
            </div>
          )}

          <div className="location-selector">
            <div className="selector-group">
              <label className="selector-label">
                <Map size={18} />
                Select Region
              </label>
              <select className="selector-input" onChange={handleRegionChange} value={selectedRegion}>
                <option value="">-- Select Region --</option>
                {Object.keys(regionsWithAreas).map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>
            <div className="selector-group">
              <label className="selector-label">
                <Building size={18} />
                Select Area
              </label>
              <select
                className="selector-input"
                onChange={(e) => setSelectedArea(e.target.value)}
                value={selectedArea}
                disabled={!selectedRegion}
              >
                <option value="">-- Select Area --</option>
                {selectedRegion &&
                  regionsWithAreas[selectedRegion].map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Admin view with no brand selected - show all brands pricing */}
          {adminView &&
            !selectedBrand &&
            selectedRegion &&
            selectedArea &&
            Object.keys(allBrandsPricing).length > 0 && (
              <div className="all-brands-pricing">
                <div className="pricing-card">
                  <div className="pricing-card-header">
                    <h3>
                      All Brands Pricing for {selectedArea}, {selectedRegion}
                    </h3>
                  </div>
                  <div className="pricing-card-body">
                    <div className="pricing-table-wrapper">
                      <table className="pricing-table">
                        <thead>
                          <tr>
                            <th>Brand</th>
                            {grades.map((grade) => (
                              <th key={grade}>{grade}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys(allBrandsPricing).map((brand) => (
                            <tr key={brand}>
                              <td className="brand-name">{brand}</td>
                              {grades.map((grade) => (
                                <td key={`${brand}-${grade}`}>
                                  {allBrandsPricing[brand] && allBrandsPricing[brand][grade] ? (
                                    <div className="price-cell">
                                      <div className="price-value">₹{allBrandsPricing[brand][grade].price}</div>
                                      <div className="delivery-time">
                                        <Truck size={14} />
                                        {allBrandsPricing[brand][grade].deliveryTime}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="not-available">N/A</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Admin view with brand selected or supplier view */}
          {selectedRegion && selectedArea && (adminView ? selectedBrand : true) && (
            <div className="brand-pricing-update">
              <div className="pricing-card">
                <div className="pricing-card-header">
                  <h3>Update Pricing for {supplierBrand}</h3>
                </div>
                <div className="pricing-card-body">
                  {pricingInfo[supplierBrand] ? (
                    <div>
                      <div className="grade-selector">
                        <label className="selector-label">
                          <Layers size={18} />
                          Select Grade
                        </label>
                        <select
                          className="selector-input"
                          onChange={(e) => {
                            setSelectedGrade(e.target.value)
                            setShowHistory(false)
                          }}
                          value={selectedGrade}
                        >
                          <option value="">-- Select Grade --</option>
                          {grades.map((grade) => (
                            <option key={grade} value={grade}>
                              {grade}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedGrade && (
                        <div className="grade-pricing-panel">
                          <div className="panel-header">
                            <h4>Update {selectedGrade} Pricing</h4>
                            <button
                              className="history-toggle-button"
                              onClick={toggleHistory}
                              title="View Price History"
                            >
                              <History size={16} />
                              {showHistory ? "Hide History" : "Show History"}
                            </button>
                          </div>

                          {/* Current Price Display */}
                          <div className="current-price-display">
                            <div className="price-display-row">
                              <div className="price-display-column">
                                <h5>Current Price</h5>
                                <div className="price-value">
                                  ₹{pricingInfo[supplierBrand][selectedGrade]?.price || "N/A"}
                                </div>
                              </div>
                              <div className="price-display-column">
                                <h5>Delivery Time</h5>
                                <div className="delivery-value">
                                  {pricingInfo[supplierBrand][selectedGrade]?.deliveryTime || "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Price History */}
                          {showHistory && (
                            <div className="price-history-section">
                              <h5>Price History</h5>
                              {Array.isArray(priceHistory) && priceHistory.length > 0 ? (
                                <div className="history-table-wrapper">
                                  <table className="history-table">
                                    <thead>
                                      <tr>
                                        <th>Date</th>
                                        <th>Previous Price</th>
                                        <th>New Price</th>
                                        <th>Delivery Time</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {priceHistory.map((history, index) => (
                                        <tr key={index}>
                                          <td>{new Date(history.timestamp).toLocaleDateString()}</td>
                                          <td>₹{history.previousPrice}</td>
                                          <td className="price-change-cell">
                                            ₹{history.newPrice}
                                            {getPriceChangeIndicator(history.previousPrice, history.newPrice)}
                                          </td>
                                          <td>{history.newDeliveryTime}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="no-history-message">
                                  <Info size={16} />
                                  No price history available.
                                </div>
                              )}
                            </div>
                          )}

                          <div className="price-update-form">
                            <div className="form-group">
                              <label>Price (₹)</label>
                              <div className="input-with-icon">
                                <input
                                  type="text"
                                  className="form-input"
                                  placeholder="Enter new price"
                                  value={manualUpdates.price || ""}
                                  onChange={(e) => handleManualUpdate("price", e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="form-group">
                              <label>Delivery Time</label>
                              <div className="input-with-icon">
                                <input
                                  type="text"
                                  className="form-input"
                                  placeholder="e.g., 2-3 days"
                                  value={manualUpdates.deliveryTime || ""}
                                  onChange={(e) => handleManualUpdate("deliveryTime", e.target.value)}
                                />
                              </div>
                            </div>
                            <button className="update-button" onClick={updatePrice}>
                              <CheckCircle size={16} /> Update Price
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-data-message">
                      <Info size={20} />
                      <p>No data available for {supplierBrand}. Select a region and area to create pricing data.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BrandRates;